import type { ErrorHandlerFunction } from '@/api/generator';
import type { NotificationMessage } from '@/components/notifications/types';
import { useNotificationStore } from '@/stores/notificationStore';
import { ref, type Ref } from 'vue';
import { isApiError } from './errors';

export interface ErrorNotifyOptions {
  showNotificationInToast: boolean;
  stopPropagation: boolean;
  notificationMessage: Ref<NotificationMessage | undefined>;
  closable: boolean;
}

export function useStoreErrorNotification(
  notifyOptions: ErrorNotifyOptions = {
    showNotificationInToast: true,
    stopPropagation: true,
    notificationMessage: ref(),
    closable: false,
  },
) {
  const errorHandler: ErrorHandlerFunction = (error) => {
    if (isApiError(error)) {
      const innerError = error.errorResponse?.innerException;

      if (innerError) {
        notifyOptions.notificationMessage.value = {
          type: 'error',
          title: error.name,
          message: innerError.message ?? '',
          closable: notifyOptions.closable,
        };
      } else {
        notifyOptions.notificationMessage.value = {
          type: 'error',
          title: error.name,
          message: error.message ?? '',
          closable: notifyOptions.closable,
        };
      }
    } else {
      notifyOptions.notificationMessage.value = {
        type: 'error',
        title: error.name,
        message: error.message,
        closable: notifyOptions.closable,
      };
    }

    if (notifyOptions.showNotificationInToast)
      useNotificationStore().notify(notifyOptions.notificationMessage.value, { show: false });

    return notifyOptions.stopPropagation ? undefined : error;
  };

  const clearError = () => {
    notifyOptions.notificationMessage.value = undefined;
  };

  return {
    errorHandler,
    notificationMessage: notifyOptions.notificationMessage,
    clearError,
  };
}
